import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';

const useRedeemEventTicket = () => {
  const queryClient = useQueryClient();
  const StoreApi = useStoreApi();

  const redeemTicket = useCallback(
    async ({
      ticketId,
      causeId,
      accessCode,
    }: {
      ticketId: string;
      causeId: string;
      accessCode?: string;
    }) => {
      const { data } = await StoreApi.put(`/tickets/${ticketId}/redeem`, {
        ticket_id: ticketId,
        cause_id: causeId,
        access_code: accessCode,
      });
      return data;
    },
    [StoreApi]
  );

  return useMutation({
    mutationFn: redeemTicket,
    onSettled: (data, error, { causeId }) => {
      void queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).redeemable.all,
      });
    },
  });
};

const useMarkEventTicketUnused = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const markUnused = useCallback(
    async ({
      ticketId,
      causeId,
      accessCode,
    }: {
      ticketId: string;
      causeId: string;
      accessCode?: string;
    }) => {
      const { data } = await StoreApi.put(`/tickets/${ticketId}/mark_unused`, {
        ticket_id: ticketId,
        cause_id: causeId,
        access_code: accessCode,
      });
      return data;
    },
    [StoreApi]
  );

  return useMutation({
    mutationFn: markUnused,
    onSettled: (data, error, { causeId }) => {
      void queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).redeemable.all,
      });
    },
  });
};

type BulkRedeemTicketRequest = {
  ticketIds: string[];
  causeId: string;
  accessCode?: string;
};

const useBulkRedeemEventTicket = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const redeemTicket = useCallback(
    async ({ ticketIds, causeId, accessCode }: BulkRedeemTicketRequest) => {
      const { data } = await StoreApi.put(`/tickets/bulk/redeem`, {
        ticket_ids: ticketIds,
        cause_id: causeId,
        access_code: accessCode,
      });
      return data;
    },
    [StoreApi]
  );

  return useMutation({
    mutationFn: redeemTicket,
    onSettled: (data, error, { causeId }) => {
      void queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).redeemable.all,
      });
    },
  });
};

export { useBulkRedeemEventTicket, useMarkEventTicketUnused, useRedeemEventTicket };
